import { observable, computed, action } from 'mobx';
import { ProductAddedUseCase } from '../use-cases/ProductAddedUseCase';
import { AddToBagErrors } from '../constants/AddToBagErrors';
import { ProductSelectedUseCase } from '../use-cases/ProductSelectedUseCase';
import { on, events, emit } from '@estee/elc-service-bus';
import { ProductGridService } from '../domain/services/ProductGridService';
import { ProductRepository } from '../repositories/ProductRepository';
import { CtaTypes } from '../constants/CTATypes';
import { InventoryStatus } from '../constants/InventoryStatuses';
import { DateFormatter } from '@estee/elc-formatters';
import { IAddToBag, IProductCTAConfig, IWaitlist } from '../interfaces/IProductCTA';
import { preorderDatePlaceholderRegex } from '../views/product-preorder/PreorderMessage';
import { Sku } from '~domain/entities/Sku';
import { Product } from '~domain/entities/Product';
import { ViewController } from '@estee/elc-service';
import { QueryFragments } from '~api/QueryFragments';
import { IFeatures } from '~constants/Features';
import { ITranslationsOverwrites } from 'src/exported/product-grid-wrapper/ProductGridWrapper';

interface IProductCTAViewControllerConfig {
    ctaType?: string;
    enableNotifyMe?: boolean;
    features?: IFeatures;
    isCoreSite?: boolean;
    isDisplayedOnSticky?: boolean;
    preorderDateFormat?: string;
    positionIndex?: number;
    showPriceOnStickyAddToBag?: boolean;
    addToBag?: IAddToBag;
    skuId?: string;
    selectedSku?: Sku;
    translationsOverwrites?: ITranslationsOverwrites;
    waitlist?: IWaitlist;
    closeModalAction?(): void;
}

type ISkuId = string;
type ISkuBaseId = number;
interface IItemsCollectionInfo {
    productQtys: { [skuId in ISkuId | ISkuBaseId]: number };
}

export class ProductCTAViewController extends ViewController {
    @observable public addingToBag: boolean = false;
    // if the add to bag fails in the first instance, will persist the disabled state of the button
    @observable public disableAddToBag: boolean = false;
    @observable public isShowingSuccessLabel: boolean = false;
    @observable public productGridService: ProductGridService;
    @observable public isLoaded: boolean = false;
    @observable product: Product;
    @observable private queryFragments: QueryFragments;
    @observable private skuId: string;
    @observable private isOnCollection: boolean = false;
    private config: IProductCTAViewControllerConfig;
    private productRepository: ProductRepository;
    private dateFormatter: DateFormatter;

    constructor(
        productGridService: ProductGridService,
        productRepository: ProductRepository,
        dateFormatter: DateFormatter,
        queryFragments: QueryFragments
    ) {
        super();

        this.productGridService = productGridService;
        this.productRepository = productRepository;
        this.dateFormatter = dateFormatter;
        this.queryFragments = queryFragments;
    }

    @action
    public initialize = (config: IProductCTAConfig) => {
        const ctaConfig: IProductCTAViewControllerConfig = {};
        ({
            ctaType: ctaConfig.ctaType,
            enableNotifyMe: ctaConfig.enableNotifyMe,
            isCoreSite: ctaConfig.isCoreSite,
            selectedSku: ctaConfig.selectedSku,
            translationsOverwrites: ctaConfig.translationsOverwrites,
            waitlist: ctaConfig.waitlist,
            isDisplayedOnSticky: ctaConfig.isDisplayedOnSticky,
            showPriceOnStickyAddToBag: ctaConfig.showPriceOnStickyAddToBag,
            addToBag: ctaConfig.addToBag,
            closeModalAction: ctaConfig.closeModalAction
        } = config);

        this.config = ctaConfig;
        this.setSelectedSkuId(ctaConfig.selectedSku?.skuId as string);
    };

    @action
    public loaded = async (selectedSku?: Sku) => {
        if (selectedSku && selectedSku.parentProduct) {
            this.product = selectedSku.parentProduct;
        } else {
            this.queryFragments.setFeatures(<IFeatures>this.config.features);
            const queryFragments = this.queryFragments.queryFragments;

            this.product = await this.productRepository.getProductBySkuId(this.selectedSku.skuId, {
                queryFragments
            });
        }

        this.isLoaded = true;
    };

    @action
    public setSelectedSkuId = (skuId: string) => {
        this.skuId = skuId;
    };

    @action
    public setAddingToBag = (status: boolean) => {
        this.addingToBag = status;
    };

    @action
    public setIsOnCollection = (status: boolean) => {
        this.isOnCollection = status;
    };

    @computed
    public get selectedSku(): Sku {
        return (
            (this.product && this.product.skus.find((sku) => sku.skuId === this.skuId)) ||
            this.product.defaultSku
        );
    }

    @computed
    get data() {
        return {
            addingToBag: this.addingToBag,
            butttonLabelOverride: this.butttonLabelOverride,
            ctaType: this.ctaType,
            disableAddToBag: this.disableAddToBag,
            enableLimitedRemaining: this.enableLimitedRemaining,
            isShowingSuccessLabel: this.isShowingSuccessLabel,
            selectedSku: this.selectedSku,
            isOnCollection: this.isOnCollection
        };
    }

    @computed
    get enableLimitedRemaining() {
        const { features } = this.config;
        const { highVelocityQuantity = 0 } = this.selectedSku;

        return (
            features &&
            features.limitedRemaining &&
            features.limitedRemaining.enabled &&
            highVelocityQuantity > 0
        );
    }

    public getStickyButtonLabel = (buttonLabel: string, buttonType?: CtaTypes) => {
        const { isDisplayedOnSticky, showPriceOnStickyAddToBag, addToBag } = this.config;
        let stickyButtonLabel = buttonLabel;

        if (buttonType === CtaTypes.ADD_TO_BAG && addToBag?.price) {
            return stickyButtonLabel;
        }

        if (isDisplayedOnSticky && showPriceOnStickyAddToBag && !!this.selectedSku.priceObject) {
            stickyButtonLabel = `${buttonLabel} - ${this.selectedSku.priceObject.priceFormatted}`;
        }

        return stickyButtonLabel;
    };

    @action
    public addToBag = async (
        skuId: string,
        quantity?: number,
        showSuccessLabel?: boolean,
        replenishment?: number
    ) => {
        this.addingToBag = true;

        try {
            const addToBagUseCase = new ProductAddedUseCase(skuId, quantity, replenishment);
            await addToBagUseCase.execute();

            if (showSuccessLabel) {
                this.isShowingSuccessLabel = true;
            }
        } catch (error) {
            if (error.message === AddToBagErrors.ITEM_QTY_MAXIMUM_REACHED) {
                this.disableAddToBag = true;
            }
        }

        this.addingToBag = false;
    };

    public preorderAvailabilityLabel = (preorderMessage: string) => {
        const { preorderDateFormat } = this.config;
        const { preorderDate } = this.selectedSku;

        return this.dateFormatter.formatDateWithPlaceholder({
            date: preorderDate,
            dateFormat: preorderDateFormat,
            message: preorderMessage,
            placeholderRegex: preorderDatePlaceholderRegex
        });
    };

    public navigateToProduct = async () => {
        const { skuId, parentProduct } = this.selectedSku;
        const product = this.product;
        const navigateToProductUseCase = new ProductSelectedUseCase(
            parentProduct.productUrl,
            skuId,
            this.config.positionIndex as number,
            product
        );
        await navigateToProductUseCase.execute();
    };

    public addSkuToCollection = async (skuId: string, quantity?: number) => {
        this.setAddingToBag(true);
        emit(events.ADD_SKU_TO_COLLECTION_TRIGGERED, { skuId, quantity });
    };

    get notifyMeEnabled() {
        const { enableNotifyMe, waitlist } = this.config;

        return enableNotifyMe || (waitlist && waitlist.enabled);
    }

    get enabledByStatus() {
        const { waitlist } = this.config;

        return waitlist && waitlist.enabledByStatus ? waitlist.enabledByStatus : [];
    }

    @computed
    get enableByInventoryStatus() {
        const { inventoryStatus } = this.selectedSku;

        return (
            this.enabledByStatus.includes(inventoryStatus) ||
            (this.enabledByStatus.length === 0 && inventoryStatus !== InventoryStatus.SoldOut)
        );
    }

    get butttonLabelOverride() {
        const { translationsOverwrites } = this.config;

        return translationsOverwrites && translationsOverwrites.ctaButtonLabel;
    }

    @computed
    get ctaType() {
        const { ctaType, isCoreSite } = this.config;
        const { inventoryStatus, isPreorderable, isShoppable, isPreorderActive } = this.selectedSku;
        const isProductAvailable = inventoryStatus === InventoryStatus.Active;
        const showNotifyMeButton =
            this.notifyMeEnabled && !isProductAvailable && this.enableByInventoryStatus;

        let productCTAType = !ctaType ? CtaTypes.DEFAULT : ctaType;

        if (
            productCTAType === CtaTypes.ADD_TO_COLLECTION ||
            productCTAType === CtaTypes.ADD_TO_BAG ||
            productCTAType === CtaTypes.DEFAULT ||
            typeof productCTAType === 'undefined'
        ) {
            if (!isProductAvailable && productCTAType === CtaTypes.ADD_TO_COLLECTION) {
                productCTAType = CtaTypes.ADD_TO_BAG;
            }

            if (!isCoreSite && showNotifyMeButton) {
                return CtaTypes.NOTIFY_ME;
            }

            if ((isPreorderable && !isShoppable) || isPreorderActive) {
                return CtaTypes.PREORDER;
            }

            return isCoreSite ? CtaTypes.FIND_IN_STORE : productCTAType;
        }

        return productCTAType;
    }

    @on(events.CART_FAILURE)
    public cartFailure = () => {
        this.isShowingSuccessLabel = false;
    };

    @on(events.CART_UPDATED)
    public cartUpdated = () => {
        this.isShowingSuccessLabel = false;
    };

    @on(events.ADD_SKU_TO_COLLECTION_SUCCESS)
    public collectionUpdated = () => {
        if (this.config && this.config.closeModalAction && !!this.addingToBag) {
            this.config.closeModalAction();
        }
    };

    @on(events.ITEMS_LOADED_INTO_COLLECTION)
    public initIsOnCollection = ({ productQtys }: IItemsCollectionInfo) => {
        if (this.isLoaded) {
            const skuId = this.selectedSku.perlgem.SKU_BASE_ID || this.selectedSku.skuId;
            this.setIsOnCollection(productQtys && skuId in productQtys);
            this.setAddingToBag(false);
        }
    };

    public sanitizeClassName(className = '') {
        return className.toLowerCase().replace(/ /g, '-');
    }
}
